// W1-7(c) — THE DOWNLOAD LOCK, KEYED TO WHAT THE GENERATORS ACTUALLY EMIT.
//
// FOUND WHILE MEASURING W1-7(c) (her PDF law) BECAUSE IT LIVES IN THE SAME FUNCTION, AND IT IS THE MORE
// SERIOUS OF THE TWO. T8-2 exists because a member downloaded a Vet spell on a Newbie membership: the
// spellbook handed the client a recipe, the client posted it to `/api/report/pdf`, and the bytes came
// back. The fix was to gate on the CONTENT rather than the route, with a set of Magic Room kinds.
//
// THE SET NEVER MATCHED THE CODE. It was written as
//     { spell, cauldron, petition, oils, herbs, keys, moses, incantation }
// and the generators emit
//     petition, spell, oils-working, keys-working, moses-working, herb-working, cauldron-working
// so `MAGIC_ROOM_KINDS.has("keys-working")` was FALSE and **FIVE OF SEVEN MAGIC ROOM GENERATORS WERE
// NOT GATED AT THE BYTES AT ALL.** Only `petition` and `spell` matched, and `spell` was covered twice
// (the `report.spell` catch-all), which is very likely why this survived: the one case anybody thought
// to re-test was the one case with a second lock behind it.
//
// It is a paywall bypass, not a cosmetic drift: the whole Magic Room except petitions and spells was
// downloadable by anyone who could post JSON. It leaked in EXACTLY the shape T8-2 was written to close.
//
// TWO CHANGES SO IT CANNOT DRIFT BACK:
//   1. Matching NORMALIZES the kind (a trailing `-working` / `-reading` is stripped) and accepts both
//      the base and the plural, so `keys`, `keys-working` and `herb`/`herbs` all lock. A future rename
//      inside that family stays locked without an edit here.
//   2. `scripts/w1-download-lock.mjs` reads the `type:` strings OUT OF THE GENERATORS in
//      `server/index.js` and asserts every Magic Room one is locked BY THIS FUNCTION. The list stops
//      being hand-maintained: a new Magic Room generator with a new type string fails the build. That
//      is the difference between a set someone remembers to update and a law that is checked.
//
// Pure and dependency-free, which is the entry requirement for the gate to load and RUN it.

// The Magic Room families, as BASE names. Both singular and plural are listed where the codebase uses
// both (`herb-working` from the generator, `herbs` from the older hand-written set).
const MAGIC_ROOM_FAMILIES = new Set([
  "spell", "spells",
  "cauldron",
  "petition", "petitions",
  "oil", "oils",
  "herb", "herbs",
  "key", "keys",
  "moses",
  "incantation", "incantations",
  "seal", "seals",
]);

// Strip the suffixes this codebase appends to a family name to make a report type. `keys-working`
// becomes `keys`; `oils-working` becomes `oils`. Anything else is returned lowercased and untouched.
export function normalizeReportKind(kind) {
  return String(kind || "").toLowerCase().trim().replace(/-(working|reading|report)$/, "");
}

// Does this report package carry Magic Room material, whoever posted it and whatever else is in it?
// The `spell` field is kept as a belt-and-braces catch-all exactly as T8-2 had it: a package that
// carries a spell body costs the Magic Room even if its `type` says something else.
export function isMagicRoomDownload(report) {
  if (!report || typeof report !== "object") return false;
  if (report.spell) return true;
  const kind = normalizeReportKind(report.type || report.kind || "");
  return MAGIC_ROOM_FAMILIES.has(kind);
}

// Exported for the gate, so the assertion runs against the real membership rather than a copy of it.
export const MAGIC_ROOM_FAMILY_NAMES = [...MAGIC_ROOM_FAMILIES];

// ---------------------------------------------------------------------------
// W1-7(d) — THE MULTI-SEAL DOWNLOAD IS COMPLETE OR IT FAILS. IT IS NEVER SILENTLY PARTIAL.
//
// HER FINDING: the seal-recommendation flow offered 3 seals plus the Seal of God, and the download
// carried only ONE seal.
//
// THE ROOT CAUSE, AND IT IS A MISSING GATE RATHER THAN A BROKEN CONVERSION. Three things lined up:
//   1. `handleReportPdf` converted the additional seals INSIDE `if (report.seal?.slug)`, so a package
//      with additionals and no primary converted none of them.
//   2. Only the PRIMARY seal's missing image failed the download. `sealPngBytes` returns null on ANY
//      failure (a missing row, a sharp error) and for an ADDITIONAL that null was never checked.
//   3. `server/pdf.js` draws an additional seal behind `if (a.pngBytes)`, so a null was SILENTLY SKIPPED.
// Net: the primary embedded, every other seal vanished without a word, and the member got a document
// that looked finished. **A PARTIAL DELIVERY THAT REPORTS SUCCESS IS THE FAILURE MODE THIS CODEBASE
// KEEPS FINDING** — it is the seal contract's "not delivered and NOT called a success" rule, and the
// download path did not have it.
//
// The pairing gate (`deliveryPairingViolations`) already checked that the Seal of God is LISTED in the
// package. It could not catch this, because listing is not carrying: the entry was there and its image
// was not. So the law below is about BYTES, deliberately, and the two gates sit next to each other.
//
// Pure, so the gate can run it on fixtures with no server and no sharp.
// ---------------------------------------------------------------------------

// Every seal the package PROMISES the member, with the role it plays. This is the manifest the delivery
// is held to: what the document says it contains.
export function sealPackageManifest(report) {
  const out = [];
  if (!report || typeof report !== "object") return out;
  const primary = report.seal;
  if (primary?.slug) out.push({ slug: primary.slug, name: primary.name || primary.slug, role: "primary" });
  const sogSlug = primary?.sealOfGod?.slug || null;
  for (const a of report.additionalSeals || []) {
    if (!a?.slug) continue;
    if (out.some((s) => s.slug === a.slug)) continue; // W1-10's dedupe instinct: never promise one twice
    out.push({ slug: a.slug, name: a.name || a.slug, role: a.slug === sogSlug ? "seal-of-god" : "additional" });
  }
  return out;
}

// THE COMPLETENESS LAW: every seal the package promises must carry embeddable bytes. Returns [] when the
// delivery is whole. `role` is reported so the message can say WHICH seal is missing rather than a count.
export function sealPackageViolations(report) {
  const out = [];
  for (const s of sealPackageManifest(report)) {
    const carried = s.role === "primary"
      ? report?.seal?.pngBytes
      : (report?.additionalSeals || []).find((a) => a?.slug === s.slug)?.pngBytes;
    if (!carried || !(carried.length > 0)) {
      out.push(`${s.name} (${s.role}) is promised by this package but carries no embeddable image.`);
    }
  }
  return out;
}

// How many seals a member should COUNT in the finished document. Used by the probe so the assertion is
// "she gets the three she was offered plus the Seal of God", not "the file is bigger than nothing".
export function expectedSealCount(report) {
  return sealPackageManifest(report).length;
}
